import { getOidcAccount } from '@/api/auth-token';
import { AppShell } from '@/components/AppShell';
import { LoadingScreen } from '@/components/LoadingScreen';
import { RequirePermissions } from '@/components/RequirePermissions';
import { signIn } from '@/lib/auth-actions';
import { encodeReturnUrl } from '@/lib/return-url';
import type * as React from 'react';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

/**
 * Pathless layout route for the authed sub-tree. Auth gate reads the OIDC
 * sync account mirror:
 *
 *  1. No cached user → `<LoadingScreen/>` paints immediately and
 *     `signIn(encodeReturnUrl(location))` is fired from an effect. The
 *     returnUrl is encoded into `signinRedirect`'s `state` so `main.tsx`'s
 *     `initOidc` + `restoreDeepLink` can restore the deep link after the
 *     Ping round-trip. No `/api/auth/me` request fires in this branch — the
 *     synchronous account check happens before React Query has a chance to
 *     mount, so the boot-time unauth case never races a 401.
 *  2. Cached user → `<AppShell><RequirePermissions><Outlet/></Require
 *     Permissions></AppShell>`. `RequirePermissions` enforces the route
 *     handle's `requiredPermissions` against `usePrincipal()`; the
 *     mid-session 401 case is handled by `AuthDriver`, mounted once in
 *     `App` and the sole observer that fires `signIn` from a 401.
 *
 * Idempotency: the `_redirecting` module flag in `lib/oidc.ts` replaces
 * MSAL's native `inProgress` state machine. `signinRedirect` navigates the
 * page away, so a re-run of this effect (React `StrictMode` dev
 * double-invoke, dep change in the brief window before the browser tears
 * down) short-circuits on that flag.
 *
 * Open-redirect defence lives in `lib/return-url.ts`; `encodeReturnUrl`
 * is the matching encoder for `safeDecodeReturnUrl` in `auth-actions.ts`.
 */
export function AuthedLayout(): React.ReactElement | null {
  const account = getOidcAccount();
  const location = useLocation();

  useEffect(() => {
    if (account) return;
    void signIn(encodeReturnUrl(location));
  }, [account, location]);

  if (!account) return <LoadingScreen />;

  return (
    <AppShell>
      <RequirePermissions>
        <Outlet />
      </RequirePermissions>
    </AppShell>
  );
}
